<template>
  <div>
    <label class="flex flex-col text-16">
      <div v-if="label" class="flex justify-between items-end" :class="labelClass">
        <p>{{ label }}</p>
        <router-link
          v-if="forgotPassword"
          to="/reset-password"
          class="text-13 md:text-opacity-50 hover:text-opacity-100 font-normal"
        >
          Forgot password?
        </router-link>
      </div>
      <div class="relative">
        <input
          ref="inputRef"
          v-model="value"
          :type="type"
          :placeholder="placeholder"
          class="w-full rounded-4 outline-none focus:shadow-signup"
          :class="[
            hasBackground ? isDarkMode ? 'bg-hover text-app-color' : 'bg-grey-400' : '',
            inputClasses,
            errorMessage && 'shadow-error']"
          :disabled="disabled"
          :readonly="readonly"
          @blur="$emit('blur')"
          @focus="$emit('focus')"
          @input="onInput"
        >
        <div v-if="showPasswordPower" class=" mt-10 flex items-center">
          <div v-if="value.length >= 1" class="flex items-center">
            <div class="mr-5 w-30 h-4 rounded-full" :class="passwordPower.level < 2 ? 'bg-red' : color" />
            <div class="mr-5 w-30 h-4 rounded-full" :class="color" />
            <div class="mr-10 w-30 h-4 rounded-full" :class="passwordPower.level > 2 ? 'bg-green-800' : 'bg-grey-700'" />
          </div>
          <p class="text-14 text-grey-275">{{ passwordPower.desc }}</p>
        </div>
      </div>
    </label>
  </div>
</template>

<script>
import { defineComponent, ref, computed, reactive, onMounted } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'InputModule',

  props: {
    label: { type: String, default: '' },
    type: { type: String, default: 'text' },
    name: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    hasBackground: { type: Boolean, default: true },
    inputClasses: { type: String, default: 'px-13 py-9' },
    labelClass: { type: String, default: 'md:mx-5 mb-10' },
    disabled: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    modelValue: { type: String, default: '' },
    errorMessage: { type: String, default: null },
    forgotPassword: { type: Boolean, default: false },
    isNeedLink: { type: Boolean, default: false },
    showPasswordPower: { type: Boolean, default: false }
  },

  emits: ['update:modelValue', 'clearErrorMessage', 'blur', 'focus', 'input'],

  setup (props, { emit }) {
    const { getters } = useStore()

    const inputRef = ref(null)

    const isDarkMode = computed(() => getters.isDarkMode)

    const passwordPower = reactive({
      level: 0,
      desc: 'At least 8 characters.'
    })

    const value = computed({
      get: () => props.modelValue,
      set: (val) => {
        emit('update:modelValue', val)
        emit('clearErrorMessage', props.name)
      }
    })

    const color = computed(() => {
      return passwordPower.level === 2 ? 'bg-yellow-600' : passwordPower.level > 2 ? 'bg-green-800' : 'bg-grey-700'
    })

    function focus () {
      inputRef.value.focus()
    }

    function onInput (e) {
      emit('input', e.target.value)
      checkPasswordStrength(e.target.value)
    }

    onMounted(() => {
      if (props.showPasswordPower) checkPasswordStrength(value.value)
    })

    function checkPasswordStrength (password) {
      var regex = ['[a-z]', '[0-9]', '[$@$!%*#?&]']

      var level = 0

      for (var i = 0; i < regex.length; i++) {
        if (new RegExp(regex[i]).test(password)) {
          level++
        }
      }

      if (password.length < 8) {
        passwordPower.level = 0
        passwordPower.desc = 'At least 8 characters.'
      } else {
        if (level > 2 && password.length >= 14) {
          passwordPower.level = 3
          passwordPower.desc = 'strong password'
        } else if (level > 1 && password.length >= 9) {
          passwordPower.level = 2
          passwordPower.desc = 'okay password'
        } else {
          passwordPower.level = 1
          passwordPower.desc = 'weak password'
        }
      }
    }

    return {
      // computed
      isDarkMode,
      value,
      color,
      passwordPower,
      // refs
      inputRef,
      // functions
      focus,
      onInput
    }
  }
})
</script>
